import React, { useState } from 'react';
import DropdownMenu from './components/DropdownMenu';
import { API_URL } from './Config';
import './MasterProjectCard.css';

function MasterProjectCard({
  masterProject,
  projects,
  isExpanded,
  onToggleExpanded,
  onUpdateMasterProject,
  onDeleteMasterProject,
  onCreateProject,
  onNavigateToProject,
  onMoveProject,
  onViewVideos,
  onRenameProject,
  onDuplicateProject,
  onDeleteProject,
  onReorderProject,
  onSyncMedia,
  onNavigateToLearning,
  onNavigateToMasterProjectLearning
}) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [editName, setEditName] = useState(masterProject.name);
  const [editDescription, setEditDescription] = useState(masterProject.description || '');

  const handleStartRename = () => {
    if (masterProject.is_default) {
      alert('Cannot rename the default "Uncategorized" master project');
      return;
    }
    setIsRenaming(true);
    setEditName(masterProject.name);
    setEditDescription(masterProject.description || '');
  };

  const handleSaveRename = async () => {
    if (!editName.trim()) {
      alert('Please enter a name');
      return;
    }

    try {
      await onUpdateMasterProject(masterProject.id, {
        name: editName.trim(),
        description: editDescription.trim()
      });
      setIsRenaming(false);
    } catch (error) {
      alert('Failed to update master project: ' + error.message);
    }
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
    setEditName(masterProject.name);
    setEditDescription(masterProject.description || '');
  };

  const handleDelete = async () => {
    if (masterProject.is_default) {
      alert('Cannot delete the default "Uncategorized" master project');
      return;
    }

    if (masterProject.project_count > 0) {
      alert(`Cannot delete master project with ${masterProject.project_count} projects. Move projects to another master project first.`);
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${masterProject.name}"?`)) {
      try {
        await onDeleteMasterProject(masterProject.id);
      } catch (error) {
        alert('Failed to delete master project: ' + error.message);
      }
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown date';
    }
  };

  const handleMoveUp = async (project, currentIndex) => {
    if (currentIndex === 0) return; // Already at the top
    if (onReorderProject) {
      // Move to one position up in the sorted array
      await onReorderProject(masterProject.id, project.id, currentIndex - 1);
    }
  };

  const handleMoveDown = async (project, currentIndex) => {
    if (currentIndex === sortedProjects.length - 1) return; // Already at the bottom
    if (onReorderProject) {
      // Move to one position down in the sorted array
      await onReorderProject(masterProject.id, project.id, currentIndex + 1);
    }
  };

  // Sort projects by sort_order to ensure correct sequence display
  const sortedProjects = [...projects].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div className={`master-project-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="master-project-header" onClick={onToggleExpanded}>
        <div className="master-project-info">
          <div className="expand-icon">
            {isExpanded ? '📂' : '📁'}
          </div>
          
          {isRenaming ? (
            <div className="rename-form" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="rename-input"
                autoFocus
                maxLength={255}
              />
              <div className="rename-actions">
                <button onClick={handleSaveRename} className="save-button">✓</button>
                <button onClick={handleCancelRename} className="cancel-button">✕</button>
              </div>
            </div>
          ) : (
            <div className="master-project-title">
              <h3>{masterProject.name}</h3>
              {masterProject.description && (
                <p className="description">{masterProject.description}</p>
              )}
            </div>
          )}
        </div>

        <div className="master-project-meta">
          <span className="project-count">
            {masterProject.project_count} project{masterProject.project_count !== 1 ? 's' : ''}
          </span>
          <div className="master-project-actions" onClick={(e) => e.stopPropagation()}>
            {!isRenaming && (
              <>
                <button
                  onClick={handleStartRename}
                  className="action-button"
                  title="Rename master project"
                  disabled={masterProject.is_default}
                >
                  ✏️
                </button>
                <button
                  onClick={(e) => onNavigateToMasterProjectLearning && onNavigateToMasterProjectLearning(e, masterProject.id)}
                  className="action-button"
                  title="Learning folders for all projects"
                >
                  📚
                </button>
                <button
                  onClick={handleDelete}
                  className="action-button delete-button"
                  title="Delete master project"
                  disabled={masterProject.is_default || masterProject.project_count > 0}
                >
                  🗑️
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="master-project-content">
          <div className="projects-header">
            <span>Projects ({projects.length})</span>
            <button
              onClick={() => onCreateProject(masterProject.id)}
              className="add-project-button"
            >
              + Add Project
            </button>
          </div>

          <div className="projects-list">
            {sortedProjects.length === 0 ? (
              <div className="empty-state">
                <p>No projects in this master project</p>
                <button
                  onClick={() => onCreateProject(masterProject.id)}
                  className="empty-state-button"
                >
                  Create First Project
                </button>
              </div>
            ) : (
              sortedProjects.map((project, index) => (
                <div key={project.id} className="project-item">
                  <div className="project-sequence">
                    <span className="sequence-number">{index + 1}.</span>
                  </div>
                  <div className="project-info">
                    <h4 onClick={() => onNavigateToProject(project.id)}>
                      {project.title}
                    </h4>
                    {project.description && (
                      <p className="project-description">{project.description}</p>
                    )}
                  </div>
                  <div className="project-reorder">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveUp(project, index);
                      }}
                      className="reorder-button"
                      title="Move up"
                      disabled={index === 0}
                    >
                      ↑
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveDown(project, index);
                      }}
                      className="reorder-button"
                      title="Move down"
                      disabled={index === sortedProjects.length - 1}
                    >
                      ↓
                    </button>
                  </div>
                  <div className="project-actions">
                    <DropdownMenu 
                      trigger={
                        <div className="three-dots-icon">
                          ⋮
                        </div>
                      }
                    >
                      <button className="dropdown-item" onClick={() => onNavigateToProject(project.id)}>
                        Edit Project
                      </button>
                      <button className="dropdown-item" onClick={(e) => onNavigateToLearning && onNavigateToLearning(e, project.id)}>
                        Learning
                      </button>
                      <button className="dropdown-item" onClick={(e) => onViewVideos && onViewVideos(e, project.id)}>
                        View Videos
                      </button>
                      <button className="dropdown-item" onClick={(e) => onSyncMedia && onSyncMedia(e, project.id)}>
                        Sync Media
                      </button>
                      <button className="dropdown-item" onClick={(e) => onRenameProject && onRenameProject(e, project)}>
                        Rename
                      </button>
                      <button className="dropdown-item" onClick={(e) => onDuplicateProject && onDuplicateProject(e, project.id)}>
                        Duplicate
                      </button>
                      <button className="dropdown-item" onClick={(e) => onMoveProject && onMoveProject(e, project)}>
                        Move to Master Project
                      </button>
                      {window.location.hostname === 'localhost' && (
                        <button className="dropdown-item danger" onClick={(e) => onDeleteProject && onDeleteProject(e, project.id, project.title)}>
                          Delete
                        </button>
                      )}
                    </DropdownMenu>
                  </div>
                </div>
              ))
            )}
          </div>

          {masterProject.created_at && (
            <div className="master-project-footer">
              <small>Created: {formatDate(masterProject.created_at)}</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MasterProjectCard;